import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * TerminalsApiService (Angular 18) — payment terminal configuration.
 *
 * Replaces apps/b2b-ui/common/services/api/terminalsApi/terminalsApi.service.js.
 *
 * Two sub-namespaces:
 *   apiService.terminals.config.{list,update,detail,terminalDetail}
 *   apiService.terminals.templates.update                          POST (not PUT)
 *
 * `templates.update(id, props)` is preserved as a POST despite the verb name
 * — that's how the legacy declared it.
 */
const TERMINAL_BASE_V2 = '/v2/api/terminal';
const TERMINAL_TEMPLATE_BASE_V2 = '/v2/api/terminal/template';

const CONFIG_DETAIL_FIELDS = ['id', 'published'] as const;
const CONFIG_TERMINAL_DETAIL_FIELDS = ['terminalId', 'published'] as const;

interface TerminalPayload {
  id?: number | string;
  terminalId: string;
  amount: unknown;
  name: string;
  description?: string;
  type: string;
  configId?: number | string;
}

@Injectable({ providedIn: 'root' })
export class TerminalsApiService {
  private readonly http = inject(HttpClient);

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${TERMINAL_BASE_V2}/list`, {
        params: buildParams(props, PAGINATION_SEARCH_FIELDS),
      }),
    );
  }

  detail(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${TERMINAL_BASE_V2}/${id}`));
  }

  update(props: TerminalPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(TERMINAL_BASE_V2, {
        id: props.id,
        terminalId: props.terminalId,
        amount: props.amount,
        name: props.name,
        description: props.description,
        type: props.type,
      }),
    );
  }

  create(props: TerminalPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(TERMINAL_BASE_V2, {
        terminalId: props.terminalId,
        amount: props.amount,
        name: props.name,
        description: props.description,
        type: props.type,
        configId: props.configId,
      }),
    );
  }

  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${TERMINAL_BASE_V2}/${id}`));
  }

  readonly config = {
    list: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${TERMINAL_BASE_V2}/assigned/${id}`)),

    update: (props: unknown): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>(`${TERMINAL_BASE_V2}/assign`, props)),

    detail: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${TERMINAL_BASE_V2}/config`, {
          params: buildParams(props, CONFIG_DETAIL_FIELDS),
        }),
      ),

    terminalDetail: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${TERMINAL_BASE_V2}/config`, {
          params: buildParams(props, CONFIG_TERMINAL_DETAIL_FIELDS),
        }),
      ),
  };

  readonly templates = {
    update: (id: number | string, props: unknown): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(`${TERMINAL_TEMPLATE_BASE_V2}/mappings/${id}`, props)),
  };
}
